import React, { useState } from 'react';
import { allHoldingsUtil, PERIOD } from './util';
import {
    setCardID,
    displayShortDateShortYear
} from '../../utils/Format/Format';
import { CARD_NAMES } from '../../utils/constants';
import styles from './AllHoldings.css';
import Card from '../../components/Card/Card';
import AgGrid from '../../components/AgGrid/AgGrid';
import ErrorMessageNoData from '../../components/ErrorMessages/ErrorMessageNoData';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import { downloadCSV } from '../../utils/csv';
import SummaryPreRender from '../../components/Summary/Summary.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import PeriodSelector from '../../components/PeriodSelector/PeriodSelector';
import {
    MF_HOLDINGS_DISCLAIMERS,
    ETF_HOLDINGS_DISCLAIMERS,
    getCsvDisclaimer
} from './holdingsDisclaimers';

import { MF_ALL_HOLDINGS, ETF_ALL_HOLDINGS } from './queries';

const AllHoldingsCard = ({ ticker, etf, firstAvailableDate }) => {
    const [selectedDate, setSelectedDate] = useState(firstAvailableDate);

    const handleChange = (event) => {
        const date = event.target.value;

        setSelectedDate(date);
    };

    return (
        <Card id={setCardID(CARD_NAMES.ALL_HOLDINGS)}>
            <GqlQueryWrapper
                query={etf ? ETF_ALL_HOLDINGS : MF_ALL_HOLDINGS}
                variables={{
                    ticker,
                    asOfDate: (selectedDate || '').split('T')[0]
                }}
                dataFunc={allHoldingsUtil(firstAvailableDate)}
                placeholder={() => <SummaryPreRender />}
                unbatch
            >
                {({
                    data: {
                        name,
                        agData,
                        csvData,
                        endOfMonthDates,
                        headers,
                        tableConfigs,
                        isMonthly,
                        asOfDate
                    }
                }) => {
                    const timePeriodTitle = isMonthly ? 'Monthly' : 'Daily';

                    const exportToCSV = () => {
                        downloadCSV(
                            csvData,
                            'allHoldings',
                            `${name} (${timePeriodTitle}) - As of Date ${displayShortDateShortYear(
                                asOfDate
                            )}\n`,
                            getCsvDisclaimer(etf, isMonthly)
                        );
                    };

                    const dataError = !agData || agData.length === 0;

                    return (
                        <React.Fragment>
                            <div className="header">
                                <div className="title-group">
                                    <div className="title">
                                        <TitleWithDescription
                                            title={`All Holdings (${timePeriodTitle})`}
                                        />
                                    </div>
                                </div>
                                <div className="captions">
                                    <div className="as-of">
                                        <PeriodSelector
                                            selectedPeriod={PERIOD.MONTHLY_VAL}
                                            customClassName="all-holdings"
                                            data={endOfMonthDates}
                                            checkData={false}
                                            selectedDate={selectedDate}
                                            handleChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <button
                                    className="export-button"
                                    onClick={exportToCSV}
                                    type="button"
                                    disabled={dataError}
                                >
                                    Download As CSV
                                </button>
                            </div>
                            {dataError ? (
                                <ErrorMessageNoData
                                    cardName={`${CARD_NAMES.ALL_HOLDINGS} (${
                                        selectedDate.split('T')[0]
                                    })`}
                                />
                            ) : (
                                <React.Fragment>
                                    <AgGrid
                                        data={{
                                            headers,
                                            rows: agData
                                        }}
                                        stickHeaders={false}
                                        gridHeight="500px"
                                        customClassName="all-holdings"
                                        tableConfigs={tableConfigs}
                                    />
                                    {!etf && (
                                        <React.Fragment>
                                            {MF_HOLDINGS_DISCLAIMERS.map(
                                                (disclaimer, index) => (
                                                    // I know index is not a good key, but this is a static array
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <p key={index}>
                                                        {disclaimer}
                                                    </p>
                                                )
                                            )}
                                        </React.Fragment>
                                    )}
                                    {/* Daily ETFs don't have disclaimers */}
                                    {etf &&
                                        isMonthly && (
                                            <React.Fragment>
                                                {ETF_HOLDINGS_DISCLAIMERS.map(
                                                    (disclaimer, index) => (
                                                        // I know index is not a good key, but this is a static array
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        <p key={index}>
                                                            {disclaimer}
                                                        </p>
                                                    )
                                                )}
                                            </React.Fragment>
                                        )}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    );
                }}
            </GqlQueryWrapper>
            <style jsx>{styles}</style>
        </Card>
    );
};

export default AllHoldingsCard;
